import React, { useMemo } from 'react';
import { formatLapSeconds } from '../../analytics/lapTime';
import { trimmedDomain } from './LapTraceChart';
import {
  buildPath,
  downsample,
  linearScale,
  niceTicks,
  padDomain,
  Point,
  seriesColour,
} from './chartUtils';
import './charts.css';

export interface SectorSeries {
  teamId: string;
  label: string;
  kartNumber: string;
  /** One sector's time per kept lap, oldest first. */
  values: number[];
}

interface SectorTraceChartProps {
  series: SectorSeries[];
  /** Shown in the accessible name, e.g. "S2". */
  sectorLabel: string;
  height?: number;
  maxPointsPerSeries?: number;
}

const MARGIN = { top: 8, right: 8, bottom: 18, left: 44 };
const VIEW_WIDTH = 720;

/**
 * One sector's time against the lap it was set on, for several teams.
 *
 * Same frame as the lap trace; a sector is a third of a lap, so differences
 * between teams are small and the median-trimmed scale matters even more here.
 */
export const SectorTraceChart: React.FC<SectorTraceChartProps> = ({
  series,
  sectorLabel,
  height = 220,
  maxPointsPerSeries = 400,
}) => {
  const model = useMemo(() => {
    const all = series.flatMap((s) => s.values);
    if (all.length < 2) return null;

    const yDomain = trimmedDomain(all);
    if (!yDomain) return null;

    const maxLaps = Math.max(...series.map((s) => s.values.length));
    const x = linearScale([1, Math.max(maxLaps, 2)], [MARGIN.left, VIEW_WIDTH - MARGIN.right]);
    const y = linearScale(padDomain(yDomain, 0.06), [height - MARGIN.bottom, MARGIN.top]);

    const paths = series.map((s) => {
      const points: Point[] = s.values.map((seconds, i) => ({
        x: x(i + 1),
        y: Math.max(MARGIN.top, Math.min(height - MARGIN.bottom, y(seconds))),
      }));
      return {
        teamId: s.teamId,
        label: s.label,
        kartNumber: s.kartNumber,
        d: buildPath(downsample(points, maxPointsPerSeries)),
      };
    });

    return {
      paths,
      yTicks: niceTicks(yDomain[0], yDomain[1], 4).map((v) => ({ v, y: y(v) })),
      xTicks: niceTicks(1, maxLaps, 6).map((v) => ({ v, x: x(v) })),
    };
  }, [series, height, maxPointsPerSeries]);

  if (!model) {
    return <div className="chart-empty">Not enough laps yet to draw a trace.</div>;
  }

  return (
    <>
      <div className="chart-legend">
        {model.paths.map((p, i) => (
          <span key={p.teamId} className="chart-legend-item">
            <span className="chart-swatch" style={{ background: seriesColour(i) }} aria-hidden />
            #{p.kartNumber} {p.label}
          </span>
        ))}
      </div>

      <svg
        className="chart"
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${sectorLabel} times by lap`}
      >
        {model.yTicks.map((t) => (
          <g key={`y${t.v}`}>
            <line
              className="chart-grid-line"
              x1={MARGIN.left}
              x2={VIEW_WIDTH - MARGIN.right}
              y1={t.y}
              y2={t.y}
            />
            <text className="chart-axis-label" x={MARGIN.left - 4} y={t.y + 3} textAnchor="end">
              {formatLapSeconds(t.v)}
            </text>
          </g>
        ))}

        {model.xTicks.map((t) => (
          <text
            key={`x${t.v}`}
            className="chart-axis-label"
            x={t.x}
            y={height - 5}
            textAnchor="middle"
          >
            {t.v}
          </text>
        ))}

        {model.paths.map((p, i) => (
          <path key={p.teamId} className="chart-series" d={p.d} style={{ stroke: seriesColour(i) }} />
        ))}
      </svg>
    </>
  );
};

export default SectorTraceChart;
